import { useCallback, useEffect, useRef, useState } from "react";
import { useLazyGetHandbookQuery } from "features/handbook/api/handbookApi";
import useDebounce from "shared/hooks/useDebounce";

type Option = { value: number; label: string };
type UseRemoteHandbookOptionsArgs = {
  slug: string;
  pageSize?: number;
  language?: "ru" | "ky";
  initialOption?: Option | null;
};

export function useRemoteHandbookOptions({
  slug,
  pageSize = 15,
  language = "ru",
  initialOption = null,
}: UseRemoteHandbookOptionsArgs) {
  const [trigger, { isFetching }] = useLazyGetHandbookQuery();
  const [options, setOptions] = useState<Option[]>(
    initialOption ? [initialOption] : []
  );
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);

  const debouncedSearch = useDebounce(search, 350);
  const latestQueryKeyRef = useRef<string>("");

  const mapToOptions = useCallback(
    (items: any[]): Option[] =>
      items.map((c: any) => ({
        value: Number(c?.id),
        label:
          language === "ru"
            ? c?.nameRu ?? String(c?.id)
            : c?.nameKy ?? String(c?.id),
      })),
    [language]
  );

  const visibleCount = initialOption
    ? options.filter((o) => o.value !== initialOption.value).length
    : options.length;

  const canLoadMore = total == null ? true : visibleCount < total;

  const load = useCallback(
    async (nextPage: number, q: string) => {
      const key = `${slug}|p=${nextPage}|q=${q}`;
      latestQueryKeyRef.current = key;
      if (nextPage > 1) setLoadingMore(true);

      try {
        const nameFilter =
          typeof q === "string" && q.trim().length > 0 ? q : null;

        const { res } = await trigger({
          slug,
          filter: { page: nextPage, size: pageSize, name: nameFilter },
        }).unwrap();

        if (latestQueryKeyRef.current !== key) return;

        const content = res?.content ?? [];
        setTotal(res?.totalElements ?? content.length);

        const nextOptions = mapToOptions(content);

        setOptions((prev) =>
          nextPage === 1
            ? initialOption
              ? [
                  initialOption,
                  ...nextOptions.filter((o) => o.value !== initialOption.value),
                ]
              : nextOptions
            : [
                ...prev,
                ...nextOptions.filter(
                  (n) => !prev.some((p) => p.value === n.value)
                ),
              ]
        );

        setPage(nextPage);
      } finally {
        setLoadingMore(false);
      }
    },
    [trigger, slug, pageSize, mapToOptions, initialOption]
  );

  useEffect(() => {
    setOptions(initialOption ? [initialOption] : []);
    setPage(1);
    setTotal(null);
    load(1, debouncedSearch);
  }, [slug, debouncedSearch, initialOption, load]);

  const fetchMore = useCallback(() => {
    if (loadingMore || !canLoadMore) return;
    load(page + 1, debouncedSearch || "");
  }, [loadingMore, canLoadMore, load, page, debouncedSearch]);

  return {
    options,
    isFetching,
    loadingMore,
    search,
    setSearch,
    fetchMore,
    canLoadMore,
    reload: () => load(1, debouncedSearch),
  };
}
